import {
  type KeyboardEventHandler,
  type MouseEventHandler,
  type RefObject,
  useCallback,
} from "react";

function isClickableElement(element: HTMLElement): boolean {
  const tagName = element.tagName.toLowerCase();

  // Check for native clickable elements
  if (tagName === "a" || tagName === "button") {
    return true;
  }

  // Check for ARIA roles
  const role = element.getAttribute("role");
  if (role === "button" || role === "link") {
    return true;
  }

  // Check if element has onclick handler and is focusable
  if (element.onclick && element.tabIndex >= 0) {
    return true;
  }

  return false;
}

function findFirstClickableElement(container: HTMLElement): HTMLElement | null {
  // Use a TreeWalker for efficient DOM traversal
  const walker = document.createTreeWalker(container, NodeFilter.SHOW_ELEMENT, {
    acceptNode: (node) => {
      if (node instanceof HTMLElement && isClickableElement(node)) {
        return NodeFilter.FILTER_ACCEPT;
      }
      return NodeFilter.FILTER_SKIP;
    },
  });

  const firstClickable = walker.nextNode() as HTMLElement | null;
  return firstClickable;
}

export function useClickWithin<T extends HTMLElement>(
  ref: RefObject<T | null>,
): MouseEventHandler<T> & KeyboardEventHandler<T> {
  return useCallback<MouseEventHandler<T> & KeyboardEventHandler<T>>(
    (event) => {
      let element = event.target as HTMLElement | null;
      const containerElement = ref.current;

      if (!containerElement) {
        return;
      }

      // First, check if the clicked element itself is clickable
      if (element && isClickableElement(element)) {
        // Let the natural click event proceed
        return;
      }

      // Traverse up the DOM tree
      while (element && element !== containerElement) {
        // Check if we've reached a clickable element
        if (isClickableElement(element)) {
          // Prevent default and trigger its click event
          event.preventDefault();
          element.click();
          return;
        }

        element = element.parentElement;
      }

      // If we've reached the container without finding a clickable element,
      // find the first clickable element within the container
      const firstClickable = findFirstClickableElement(containerElement);
      if (firstClickable) {
        event.preventDefault();
        firstClickable.click();
      }
    },
    [ref],
  );
}
